'use client';

import type { ReactNode } from 'react';

type SettingsChipGroupProps = {
  /**
   * Accessible name for the group. Required: every call site is a set of related
   * choices (days of the week, pet types, entry options) whose individual
   * controls are named for one option each, so the set itself needs a name for
   * the question being asked.
   */
  ariaLabel: string;
  children: ReactNode;
  className?: string;
};

/**
 * The prototype's `.chips`: a wrapping horizontal run of small related controls.
 *
 * A `fieldset` NAMED BY `aria-label`, with no `legend`. The element carries the
 * grouping semantics for free, and the name has to come from the attribute
 * because a settings row already draws the visible label in its left column — a
 * legend would repeat it on screen.
 *
 * No margin, padding or border utilities here, and none are wanted: Bootstrap's
 * reboot already zeroes all three on `fieldset`, unlayered, so a layered utility
 * of the same meaning would lose. Only layout is set, which reboot does not
 * touch.
 *
 * The controls inside are the caller's; every call site today passes DS
 * `Checkbox`es, which carry their own labels and their own checked state, so
 * this file holds no selection logic at all.
 */
export const SettingsChipGroup = ({ ariaLabel, children, className }: SettingsChipGroupProps) => (
  <fieldset
    data-lula-ds=""
    aria-label={ariaLabel}
    className={`flex min-w-0 flex-wrap items-center gap-x-space-16 gap-y-space-8 font-sans${
      className ? ` ${className}` : ''
    }`}
  >
    {children}
  </fieldset>
);

export default SettingsChipGroup;
